import { createContext, PropsWithChildren, useMemo, useRef } from 'react'
import { SwitchTransition, Transition } from 'react-transition-group'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { useRouter } from 'next/router'

import { cn } from '@/lib/utils'

export const TransitionContext = createContext<undefined>(undefined)

const TransitionProvider = ({ children }: PropsWithChildren) => {
  // console.log('TransitionProvider')
  const router = useRouter()
  const nodeRef = useRef<HTMLElement>(null!)
  const contextValue = useMemo(() => undefined, [])

  const { context, contextSafe } = useGSAP({ scope: nodeRef })

  const handleEnterTransition = contextSafe(() => {
    const selector = context.selector || gsap.utils.selector(nodeRef.current)
    const solidLight = selector('[data-motion-solid-light]')[0]
    const solidDark = selector('[data-motion-solid-dark]')[0]

    gsap
      .timeline()
      .set([solidDark, solidLight], { display: 'block' }, 0)
      .to(
        [solidDark, solidLight],
        {
          yPercent: -100,
          duration: 1.1,
          ease: 'power3.out',
          stagger: { amount: 0.1 }
        },
        0.1
      )
  })

  const handleLeaveTransition = contextSafe(() => {
    const selector = context.selector || gsap.utils.selector(nodeRef.current)
    const solidLight = selector('[data-motion-solid-light]')[0]
    const solidDark = selector('[data-motion-solid-dark]')[0]

    gsap
      .timeline()
      .set([solidLight, solidDark], { display: 'block' })
      .fromTo(
        [solidLight, solidDark],
        { yPercent: 100 },
        { yPercent: 0, duration: 1.1, ease: 'power3.in', stagger: { amount: 0.1 } }
      )
  })

  return (
    <TransitionContext.Provider value={contextValue}>
      <SwitchTransition>
        <Transition
          // appear
          key={router.pathname}
          nodeRef={nodeRef}
          timeout={1400}
          onEnter={handleEnterTransition}
          onExit={handleLeaveTransition}
          unmountOnExit
        >
          <main ref={nodeRef} key={router.pathname} className="static">
            {children}
            <div
              className={cn([
                'fixed left-0 top-0 z-mask hidden h-full w-full',
                'before:absolute before:left-0 before:top-0 before:h-full before:w-full before:bg-black-light',
                'after:absolute after:left-0 after:top-0 after:h-full after:w-full after:bg-text-accent-light after:opacity-50'
              ])}
              data-motion-solid-light
            />
            <div
              className="fixed left-0 top-0 z-mask hidden h-full w-full bg-black-light"
              data-motion-solid-dark
            />
          </main>
        </Transition>
      </SwitchTransition>
    </TransitionContext.Provider>
  )
}

export default TransitionProvider
